import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const POPOVER_WIDTH_PX = 328
const POPOVER_ESTIMATED_HEIGHT_PX = 360
const VIEWPORT_GAP_PX = 8

function parseYmd(value) {
  const trimmed = String(value ?? '').trim()
  if (!trimmed) return null
  const parsed = parseISO(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function toYmd(date) {
  return format(date, 'yyyy-MM-dd')
}

/**
 * Meeting-builder date field: full-width trigger + modern popover calendar (no native date UI).
 * Popover renders in a portal with auto-flip so it stays fully visible and clickable.
 */
export default function BuilderDatePicker({
  id: idProp,
  label,
  value = '',
  onChange,
  disabled = false,
  minDate,
  placeholder = 'Choose a date',
  className = '',
  /** `auto` flips above when there is not enough room below; `above` always opens upward. */
  preferPlacement = 'auto',
}) {
  const autoId = useId()
  const triggerId = idProp ?? `builder-date-${autoId}`
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)
  /** Locked above/below once the popover opens; cleared on close. */
  const placementRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })

  const selectedDate = useMemo(() => parseYmd(value), [value])
  const minDay = useMemo(() => {
    const parsed = parseYmd(minDate)
    return parsed ? startOfDay(parsed) : null
  }, [minDate])

  const [visibleMonth, setVisibleMonth] = useState(() => selectedDate ?? startOfDay(new Date()))

  useEffect(() => {
    if (selectedDate) setVisibleMonth(selectedDate)
  }, [selectedDate])

  const close = useCallback(() => {
    placementRef.current = null
    setOpen(false)
  }, [])

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return

    const rect = trigger.getBoundingClientRect()
    const popoverHeight = popoverRef.current?.offsetHeight ?? POPOVER_ESTIMATED_HEIGHT_PX
    const popoverWidth = popoverRef.current?.offsetWidth ?? POPOVER_WIDTH_PX

    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_GAP_PX
    const spaceAbove = rect.top - VIEWPORT_GAP_PX

    let openAbove = placementRef.current
    if (openAbove == null) {
      openAbove = preferPlacement === 'above'
      if (preferPlacement === 'auto') {
        if (spaceBelow < popoverHeight && spaceAbove >= popoverHeight) {
          openAbove = true
        } else if (spaceBelow < popoverHeight && spaceAbove > spaceBelow) {
          openAbove = true
        } else {
          openAbove = false
        }
      }
      placementRef.current = openAbove
    }

    let top = openAbove
      ? rect.top - popoverHeight - VIEWPORT_GAP_PX
      : rect.bottom + VIEWPORT_GAP_PX
    top = Math.max(VIEWPORT_GAP_PX, Math.min(top, window.innerHeight - popoverHeight - VIEWPORT_GAP_PX))

    let left = rect.left
    left = Math.max(VIEWPORT_GAP_PX, Math.min(left, window.innerWidth - popoverWidth - VIEWPORT_GAP_PX))

    setCoords({ top, left })
  }, [preferPlacement])

  useLayoutEffect(() => {
    if (!open) return undefined
    updatePosition()
    const frame = requestAnimationFrame(updatePosition)
    return () => cancelAnimationFrame(frame)
  }, [open, visibleMonth, updatePosition])

  useEffect(() => {
    if (!open) return undefined
    const handleReposition = () => updatePosition()
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)
    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return undefined
    const handlePointerDown = (event) => {
      if (triggerRef.current?.contains(event.target)) return
      if (popoverRef.current?.contains(event.target)) return
      close()
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [close, open])

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(visibleMonth)
    const monthEnd = endOfMonth(visibleMonth)
    return eachDayOfInterval({
      start: startOfWeek(monthStart),
      end: endOfWeek(monthEnd),
    })
  }, [visibleMonth])

  const displayLabel = selectedDate
    ? format(selectedDate, 'EEEE, MMMM d, yyyy')
    : placeholder

  const isDayDisabled = useCallback(
    (day) => {
      if (!minDay) return false
      return isBefore(startOfDay(day), minDay)
    },
    [minDay]
  )

  const handleSelectDay = (day) => {
    if (isDayDisabled(day)) return
    onChange(toYmd(day))
    close()
  }

  const openPicker = () => {
    if (disabled) return
    placementRef.current = null
    setVisibleMonth(selectedDate ?? minDay ?? startOfDay(new Date()))
    setOpen(true)
  }

  const popover = open && !disabled ? (
    <div
      ref={popoverRef}
      role="dialog"
      aria-modal="false"
      aria-label={label || 'Choose a date'}
      className="fixed w-[min(20.5rem,calc(100vw-1rem))] overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-xl ring-1 ring-black/5"
      style={{ top: coords.top, left: coords.left, zIndex: 9999 }}
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setVisibleMonth((prev) => subMonths(prev, 1))}
          className="rounded-xl p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <p className="text-sm font-semibold text-gray-900 sm:text-base">
          {format(visibleMonth, 'MMMM yyyy')}
        </p>
        <button
          type="button"
          onClick={() => setVisibleMonth((prev) => addMonths(prev, 1))}
          className="rounded-xl p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAY_LABELS.map((weekday) => (
          <span
            key={weekday}
            className="py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-gray-400 sm:text-xs"
          >
            {weekday}
          </span>
        ))}
        {calendarDays.map((day) => {
          const inMonth = isSameMonth(day, visibleMonth)
          const selected = selectedDate ? isSameDay(day, selectedDate) : false
          const today = isSameDay(day, new Date())
          const dayDisabled = isDayDisabled(day)
          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={dayDisabled}
              onClick={() => handleSelectDay(day)}
              className={[
                'aspect-square w-full rounded-xl text-sm font-medium transition',
                !inMonth ? 'text-gray-300' : dayDisabled ? 'cursor-not-allowed text-gray-300' : 'text-gray-800',
                selected
                  ? 'text-white shadow-sm'
                  : today && inMonth && !dayDisabled
                    ? 'ring-2 ring-[#16c1b2]/30 ring-offset-1 hover:bg-[#e4faf8]'
                    : inMonth && !dayDisabled
                      ? 'hover:bg-gray-100'
                      : '',
              ].join(' ')}
              style={selected ? { background: 'linear-gradient(135deg, #16c1b2 0%, #0b6fa4 100%)' } : undefined}
            >
              {format(day, 'd')}
            </button>
          )
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
        <button
          type="button"
          onClick={() => {
            const today = startOfDay(new Date())
            if (!isDayDisabled(today)) handleSelectDay(today)
          }}
          className="rounded-lg px-2 py-1 text-xs font-medium text-[#16c1b2] transition hover:bg-[#e4faf8] sm:text-sm"
        >
          Today
        </button>
        {selectedDate ? (
          <button
            type="button"
            onClick={() => {
              onChange('')
              close()
            }}
            className="rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 sm:text-sm"
          >
            Clear
          </button>
        ) : null}
      </div>
    </div>
  ) : null

  return (
    <div className={className.trim()}>
      {label ? (
        <span id={`${triggerId}-label`} className="mb-2 block text-sm font-medium text-gray-700">
          {label}
        </span>
      ) : null}

      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-labelledby={label ? `${triggerId}-label` : undefined}
        onClick={openPicker}
        className={[
          'flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left shadow-sm transition',
          disabled
            ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 opacity-70'
            : open
              ? 'border-[#16c1b2]/50 bg-white ring-4 ring-[#16c1b2]/10'
              : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white focus:border-[#16c1b2]/50 focus:outline-none focus:ring-4 focus:ring-[#16c1b2]/10',
        ].join(' ')}
      >
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            disabled ? 'bg-gray-200 text-gray-400' : 'bg-[#e4faf8] text-[#0d8f84]'
          }`}
        >
          <Calendar className="h-5 w-5" aria-hidden />
        </span>
        <span className={`min-w-0 flex-1 text-sm sm:text-base ${selectedDate ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
          {displayLabel}
        </span>
      </button>

      {typeof document !== 'undefined' && popover ? createPortal(popover, document.body) : null}
    </div>
  )
}
